/* eslint-disable react/no-unstable-nested-components */
import React, { Fragment, useContext, useEffect, useState } from 'react';
import { windowWrapper } from '@clubmed/utils';
import { classnames } from 'Utils/classnames/classnames.utils';
import { useFormikContext } from 'formik';
import { FormikInput } from 'Components/frameworks/formik/FormikInput';
import { Icon } from 'Components/frameworks/icon/Icon';
import { ICONS } from 'Components/utils/icon/icons';
import { ServicesContext } from 'Context/ServicesContext';
import { t } from 'Src/i18n';
import { CHILDCARE_STOCK_THRESHOLD } from 'Utils/childcare/childcare.consts';
import { DateSchedule } from 'Components/widgets/reservation-block/DateSchedule';
import { ScheduleEnvelopeFSS } from 'Components/widgets/reservation-block/ScheduleEnvelopeFSS';
type Props = {
  stayDepartureDate: string;
  stayArrivalDate: string;
  attendeeId: string;
};

const isSingleDaySchedule = (schedule) => schedule.startDate === schedule.endDate;

export const ReservationScheduleStepFSS = ({
  stayDepartureDate: departureDate,
  stayArrivalDate: arrivalDate,
  attendeeId,
}: Props) => {
  const { scheduleReservationMessage } = useContext(ServicesContext);
  const { values, validateForm, setFieldValue } = useFormikContext();
  const [lastAvailableSchedule, setLastAvailableSchedule] = useState({});
  // show only current attendee schedules
  const filteredAttendeesValues = [values.attendees[attendeeId]];
  // find the all duration schedule if exists
  // he has a special treatment because of the childcare BY DAY
  // that is equivalent to the whole duration of stay
  useEffect(() => {
    validateForm();
    filteredAttendeesValues
      .filter(({ selected }) => !selected)
      .forEach(({ schedules: attendeeSchedules }) => {
        // const attendeeSchedules = values.attendees[attendeeId].schedules;
        attendeeSchedules.forEach(({ isSelected }, scheduleIndex) => {
          if (!isSelected) {
            return;
          }

          setFieldValue(`attendees.${attendeeId}.schedules[${scheduleIndex}].isSelected`, false);
        });
      });
    return () => {
      // when the component is unmount the validation is not triggered and the confirm button can stay disabled
      validateForm();
    };
  }, []);
  return (
    <Fragment>
      {scheduleReservationMessage && (
        <p className="mt-10 text-12 font-bold text-sienna">{scheduleReservationMessage}</p>
      )}

      {filteredAttendeesValues.map(({ schedules }) => {
        const fullStayScheduleIndex = schedules.findIndex(
          ({ startDate, endDate }) => startDate === arrivalDate && endDate === departureDate,
        );
        const fullStaySchedule = schedules[fullStayScheduleIndex];
        const attendeeSelectedSchedules = schedules.filter(({ isSelected }) => isSelected);
        const attendeePaidSchedules = schedules.filter(({ isPaid }) => isPaid);
        return (
          <div className="divide-y divide-lightGrey" key={attendeeId}>
            <div className="flex flex-wrap gap-10 py-10">
              {schedules.map(({ endDate, startDate }, scheduleIndex) => {
                // Disabling schedules frontend
                const currentChildSchedule = values.attendees[attendeeId].schedules[scheduleIndex];
                const fullStayScheduleIsPaid = fullStaySchedule.isPaid;
                const isCurrentSingleAndFullStayPaid =
                  isSingleDaySchedule(currentChildSchedule) && fullStayScheduleIsPaid;
                const isCurrentFullStayAndOneSinglePaid =
                  !isSingleDaySchedule(currentChildSchedule) &&
                  schedules.some((schedule) => isSingleDaySchedule(schedule) && schedule.isPaid);
                const isCurrentSchedulePaid =
                  !!currentChildSchedule?.isPaid || !!currentChildSchedule?.isFullyPaid;
                const isDisabled = // if current schedule is paid
                  isCurrentSchedulePaid || // if the current schedule if it's a single schedule & full stay schedule is paid
                  isCurrentSingleAndFullStayPaid || // if the current schedule is a full stay schedule & one of the single schedules is paid
                  isCurrentFullStayAndOneSinglePaid;

                // SCHEDULE PLACEHOLDER : defined here cuz it needs isDisabled
                const PlaceHolderFSS = (isChecked) => (
                  <div
                    className={classnames(
                      'flex items-center rounded-12 border-1 px-10 py-5 text-10 font-bold',
                      {
                        'border-lightGrey text-black': !isChecked,
                        'border-saffron-active bg-saffron text-black': isChecked,
                        'italic opacity-50': isDisabled,
                        'w-1/2 justify-between': startDate !== endDate,
                      },
                    )}
                    data-testid="reservationScheduleDay"
                  >
                    <DateSchedule date={startDate} />
                    {startDate !== endDate && (
                      <Fragment>
                        <div className="mx-10">{t('common.to')}</div>
                        <DateSchedule date={endDate} />
                      </Fragment>
                    )}
                  </div>
                );

                // SCHEDULES STEP
                return (
                  <ScheduleEnvelopeFSS
                    key={`${startDate}-${endDate}-Container`}
                    isFullStaySchedule={startDate === arrivalDate && endDate === departureDate}
                    InputComp={() => (
                      <FormikInput
                        isDisabled={isDisabled}
                        key={`${startDate}${endDate}`}
                        dataTestId="scheduleCheckboxItem"
                        name={`attendees.${attendeeId}.schedules[${scheduleIndex}].isSelected`}
                        type="checkbox"
                        onChange={(e) => {
                          const hasSelectedAllSingleSchedules =
                            e.target.checked &&
                            attendeeSelectedSchedules.length === schedules.length - 2;
                          const hasPaidSomeSingleSchedules = attendeePaidSchedules?.length >= 1;

                          // select all single day schedules, the full stay schedule is automatically selected
                          if (hasSelectedAllSingleSchedules && !hasPaidSomeSingleSchedules) {
                            windowWrapper.requestAnimationFrame(() => {
                              values.attendees[attendeeId].schedules.forEach(
                                ({ startDate: sDate, endDate: eDate }, scheduleIndexFE) => {
                                  if (
                                    sDate === fullStaySchedule.startDate &&
                                    eDate === fullStaySchedule.endDate
                                  ) {
                                    setFieldValue(
                                      `attendees.${attendeeId}.schedules[${scheduleIndexFE}].isSelected`,
                                      true,
                                    );
                                  } else {
                                    setFieldValue(
                                      `attendees.${attendeeId}.schedules[${scheduleIndexFE}].isSelected`,
                                      false,
                                    );
                                  }
                                },
                              );
                            });
                          }

                          // if i select the full stay schedule, all the single day schedules are automatically unselected
                          if (
                            e.target.checked &&
                            startDate === arrivalDate &&
                            endDate === departureDate
                          ) {
                            values.attendees[attendeeId].schedules.map(
                              (
                                { startDate: scheduleStartDateMp, endDate: scheduleEndDateMp },
                                scheduleIndexMp,
                              ) => {
                                // FS schedule
                                if (
                                  scheduleEndDateMp !== scheduleStartDateMp &&
                                  scheduleEndDateMp === departureDate &&
                                  scheduleStartDateMp === arrivalDate
                                ) {
                                  // setTimeout here is a hack to fix formik bug when calling setFieldValue in onChange
                                  setTimeout(() => {
                                    windowWrapper.requestAnimationFrame(() => {
                                      setFieldValue(
                                        `attendees.${attendeeId}.schedules.[${scheduleIndexMp}].isSelected`,
                                        true,
                                      );
                                    });
                                  }, 0);
                                }

                                // single day other schedules
                                if (scheduleEndDateMp === scheduleStartDateMp) {
                                  windowWrapper.requestAnimationFrame(() => {
                                    setFieldValue(
                                      `attendees.${attendeeId}.schedules[${scheduleIndexMp}].isSelected`,
                                      false,
                                    );
                                  });
                                }
                              },
                            );
                          }

                          // if you select any single day schedule, the full stay schedule is automatically deselected
                          if (e.target.checked && attendeeSelectedSchedules.length === 1) {
                            windowWrapper.requestAnimationFrame(() => {
                              values.attendees[attendeeId].schedules.forEach(
                                ({ startDate: startD, endDate: endD }, scheduleIndexFE) => {
                                  if (
                                    startD === fullStaySchedule.startDate &&
                                    endD === fullStaySchedule.endDate
                                  ) {
                                    setFieldValue(
                                      `attendees.${attendeeId}.schedules[${scheduleIndexFE}].isSelected`,
                                      false,
                                    );
                                  }
                                },
                              );
                            });
                          }

                          if (
                            !values.attendees[attendeeId].schedules[scheduleIndex].isSelected &&
                            values.attendees[attendeeId].schedules[scheduleIndex].remainingStock <=
                              CHILDCARE_STOCK_THRESHOLD
                          ) {
                            setLastAvailableSchedule({
                              isVisible: true,
                              nb: values.attendees[attendeeId].schedules[scheduleIndex]
                                .remainingStock,
                            });
                          } else {
                            setLastAvailableSchedule({});
                          }
                        }}
                        placeholderComponent={PlaceHolderFSS}
                      />
                    )}
                  />
                );
              })}
            </div>
          </div>
        );
      })}

      {lastAvailableSchedule.isVisible && (
        <p className="mt-5 flex items-center gap-5 text-14 font-bold leading-1 text-red">
          <Icon className="shrink-0" name={ICONS.WARNING} width="18px" />
          {t('booking.activities.remainingService', {
            nb: lastAvailableSchedule.nb,
          })}
        </p>
      )}
    </Fragment>
  );
};
